import type { UserState } from './user-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';

import type { Action } from './utils/state-manager';
import {
  StateStatus,
  updateResourceByParam,
  updateStatus,
  updateStatusById,
  setStatusDirtyById,
  setStateDirtyByParam,
} from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: UserState = {
  usersById: {},
  usersStateByAccountId: {},
  usersByOrgId: {},
  usersByOrgIdStatus: {},
  adminUsers: [],
  adminUsersStatus: StateStatus.notAttempted,
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_ACCOUNT_USERS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'users', 'accountId', accountId, true);
    }
    case SUCCESS('LOAD_ACCOUNT_USERS'): {
      const {
        args: { accountId },
        data,
      } = action.payload;
      const usersByUsername = convertArrayToMap(data, (u: User) => u.username);
      return updateResourceByParam(state, 'users', 'accountId', accountId, false, usersByUsername);
    }
    case FAILURE('LOAD_ACCOUNT_USERS'): {
      const { accountId } = action.payload.args;
      return updateResourceByParam(state, 'users', 'accountId', accountId, false);
    }
    case STARTING('LOAD_ORG_USERS'): {
      const { orgId } = action.payload.args;
      return updateStatusById(state, 'usersByOrgId', true, orgId);
    }
    case SUCCESS('LOAD_ORG_USERS'): {
      const { args, data } = action.payload;
      return updateStatusById(state, 'usersByOrgId', false, args.orgId, data);
    }
    case STARTING('LOAD_ADMIN_USERS'): {
      return updateStatus(state, 'adminUsers', true);
    }
    case SUCCESS('LOAD_ADMIN_USERS'): {
      const { data } = action.payload;
      return updateStatus(state, 'adminUsers', false, data);
    }
    case SUCCESS('GRANT_EMPLOYEE_ACCESS'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'usersByOrgId', orgId);
    }
    case SUCCESS('GRANT_ACCESS_TO_ORG'): {
      const { orgId, accountId } = action.payload.args;
      let newState = { ...state };
      newState = accountId
        ? setStateDirtyByParam(newState, 'users', 'accountId', accountId)
        : newState;
      return setStatusDirtyById(newState, 'usersByOrgId', orgId);
    }
    case SUCCESS('MOVE_USER'): {
      const { user: updatedUser }: { user: User } = action.payload.data;
      const { user }: { user: User } = action.payload.args;

      let newState = { ...state };
      // Set dirty new and old orgs
      const userOrgs = [...(user.orgs || []), ...(updatedUser.orgs || [])];
      const orgIds = userOrgs.map((org) => org.orgId);
      orgIds.forEach((orgId) => {
        newState = setStatusDirtyById(newState, 'usersByOrgId', orgId.toString());
      });

      // remove user from state
      delete newState.usersById[updatedUser.username];

      // set dirty new accounts to reload users
      newState = user.accountId
        ? setStateDirtyByParam(newState, 'users', 'accountId', user.accountId.toString())
        : newState;

      return updatedUser?.accountId
        ? setStateDirtyByParam(newState, 'users', 'accountId', updatedUser.accountId.toString())
        : newState;
    }
    case SUCCESS('UPDATE_USER'):
    case SUCCESS('CREATE_USER'): {
      const { args, data } = action.payload;
      const { user }: { user: UserRequest } = args;
      if (data.warningMessage) {
        return state;
      }
      let newState = { ...state };
      if (user.orgs) {
        const orgIds = user.orgs.map((org) => org.orgId);
        orgIds.forEach((orgId) => {
          newState = setStatusDirtyById(newState, 'usersByOrgId', orgId.toString());
        });
      }
      return user.accountId
        ? setStateDirtyByParam(newState, 'users', 'accountId', user.accountId.toString())
        : newState;
    }
    case SUCCESS('DISABLE_USER'): {
      const { reducerData } = action.payload.args;
      if (reducerData?.accountId) {
        return setStateDirtyByParam(state, 'users', 'accountId', reducerData.accountId);
      }
      return state;
    }
    case SUCCESS('ENABLE_USER'): {
      const { reducerData } = action.payload.args;
      if (reducerData?.accountId) {
        return setStateDirtyByParam(state, 'users', 'accountId', reducerData.accountId);
      }
      return state;
    }
    default:
      return state;
  }
};
